import { useEffect, useState } from 'react'
import { LIBELLE_PALIER, libelleAuteur, libelleChangement, libelleOptions, optionsChangees, traduireErreurJournal } from '../api/abonnement'
import { couleurOrganisation, dateHeure, dateLongue, fcfa, initiales, nombre } from '../api/format'
import { palierEffectifOrg, tarifOrganisation } from '../api/mrr'
import type { Changement, Organisation, Plan, Source } from '../api/types'
import { Badge, BadgePalier, palierDiverge, statutDe } from './Badges'
import { Icone } from './Icone'
import { Modale } from './Modale'
import { SectionAccesApi } from './SectionAccesApi'

type Historique =
  | { etat: 'chargement' }
  | { etat: 'erreur'; message: string }
  | { etat: 'pret'; lignes: Changement[] }

/**
 * Fiche d'une organisation : situation d'abonnement, bouton « Modifier
 * l'abonnement » et journal des changements. Les chiffres viennent des mêmes
 * agrégats que le tableau — aucune donnée opérationnelle du tenant.
 */
export function FicheOrg({
  org,
  plans,
  maintenant,
  source,
  versionJournal,
  surModifier,
  surFermer,
  surAccesApiChange,
  notifier,
}: {
  org: Organisation
  plans: Plan[] | null
  maintenant: Date
  source: Source
  /** Incrémenté après chaque enregistrement pour recharger le journal. */
  versionJournal: number
  surModifier: () => void
  surFermer: () => void
  /** Recharge les données de la console après un changement d'accès API ou une révocation de clé. */
  surAccesApiChange: () => Promise<void>
  notifier: (m: string) => void
}) {
  const [hist, setHist] = useState<Historique>({ etat: 'chargement' })
  const [essai, setEssai] = useState(0)

  useEffect(() => {
    let annule = false
    setHist({ etat: 'chargement' })
    source.historique(org.org_id).then(
      (lignes) => { if (!annule) setHist({ etat: 'pret', lignes }) },
      (e: unknown) => { if (!annule) setHist({ etat: 'erreur', message: traduireErreurJournal(e) }) },
    )
    return () => { annule = true }
  }, [source, org.org_id, versionJournal, essai])

  const nom = org.brand_name || org.name
  const st = statutDe(org, maintenant)
  const effectif = palierEffectifOrg(org, maintenant)
  const diverge = palierDiverge(org, maintenant)
  const tarif = plans ? tarifOrganisation(org, plans) : null

  return (
    <Modale
      variante="tiroir"
      titre={nom}
      sousTitre={org.is_operator ? 'Organisation opératrice' : org.is_white_label ? 'Marque blanche' : 'SaaS'}
      surFermer={surFermer}
    >
      <div className="fiche-id">
        <span className="avatar-org grand" style={{ background: couleurOrganisation(nom) }} aria-hidden="true">{initiales(nom)}</span>
        <div className="fiche-badges">
          <BadgePalier o={org} />
          <Badge classe={st.classe}>{st.libelle}</Badge>
        </div>
      </div>

      <h3 className="fiche-titre">Abonnement</h3>
      {org.is_operator ? (
        <p className="encart">
          <Icone nom="lock" /> L'organisation opératrice n'a pas d'abonnement : c'est nous, et la base ne la bride jamais.
          Il n'y a rien à activer ici.
        </p>
      ) : (
        <>
          <dl className="liste-def">
            <div><dt>Palier facturé</dt><dd>{LIBELLE_PALIER[org.plan_tier]}{tarif?.base != null ? ` — ${fcfa(tarif.base)} / mois` : plans ? ' — sur devis' : ''}</dd></div>
            {org.sub_status && (
              <>
                <div><dt>Options</dt><dd>{libelleOptions(org.extra_vehicles, org.billing_period)}{org.gps_devices > 0 ? ` · ${org.gps_devices} boîtier${org.gps_devices > 1 ? 's' : ''} GPS actif${org.gps_devices > 1 ? 's' : ''}` : ''}</dd></div>
                <div>
                  <dt>Prix de l'abonnement</dt>
                  <dd>
                    {tarif?.mensuel != null && tarif.du != null
                      ? `${fcfa(tarif.mensuel)} / mois${org.billing_period === 'annual' ? ` · ${fcfa(tarif.du)} par an (MRR ${fcfa(tarif.mrr)})` : ''}`
                      : plans ? 'sur devis' : '—'}
                  </dd>
                </div>
              </>
            )}
            <div><dt>Palier appliqué</dt><dd>{LIBELLE_PALIER[effectif]}{diverge ? ' (différent du palier facturé)' : ''}</dd></div>
            <div><dt>Statut</dt><dd>{st.libelle}</dd></div>
            <div><dt>Fin de période</dt><dd>{dateLongue(org.period_end)}</dd></div>
          </dl>
          <button type="button" className="btn btn-plein" onClick={surModifier} data-focus-initial>
            <Icone nom="edit" /> Modifier l'abonnement
          </button>
        </>
      )}

      <SectionAccesApi org={org} source={source} surChange={surAccesApiChange} notifier={notifier} />

      <h3 className="fiche-titre">Activité (agrégats)</h3>
      <dl className="liste-def">
        <div><dt>Membres</dt><dd>{nombre(org.members)}</dd></div>
        <div><dt>Livreurs</dt><dd>{nombre(org.drivers)}</dd></div>
        <div><dt>Véhicules</dt><dd>{nombre(org.vehicles)}</dd></div>
        <div><dt>Courses (30 j / total)</dt><dd>{nombre(org.courses_30d)} / {nombre(org.courses_total)}</dd></div>
        <div><dt>Revenu cumulé</dt><dd>{fcfa(org.revenue_fcfa)}</dd></div>
        <div><dt>Dernier paiement</dt><dd>{dateLongue(org.last_payment)}</dd></div>
        <div><dt>Créée le</dt><dd>{dateLongue(org.created_at)}</dd></div>
      </dl>

      <h3 className="fiche-titre"><Icone nom="history" /> Historique de l'abonnement</h3>
      {hist.etat === 'chargement' && <div className="sk" aria-busy="true" aria-label="Chargement de l'historique" />}
      {hist.etat === 'erreur' && (
        <p className="encart encart-alerte" role="alert">
          <Icone nom="alert" /> {hist.message}{' '}
          <button type="button" className="lien" onClick={() => setEssai((n) => n + 1)}>Réessayer</button>
        </p>
      )}
      {hist.etat === 'pret' && hist.lignes.length === 0 && (
        <p className="discret-t">Aucun changement manuel enregistré pour cette organisation.</p>
      )}
      {hist.etat === 'pret' && hist.lignes.length > 0 && (
        <ol className="journal">
          {hist.lignes.map((c) => (
            <li key={c.id}>
              <div className="journal-tete">
                <b>{libelleChangement(c)}</b>
                <span className="discret-t">{dateHeure(c.created_at)}</span>
              </div>
              <div className="discret-t">
                {libelleAuteur(c.changed_by, source.utilisateurId, c.auteur)}
                {c.period_end ? ` · fin de période ${dateLongue(c.period_end)}` : ''}
              </div>
              {optionsChangees(c) && (
                <div className="discret-t">
                  Options : {libelleOptions(c.old_extra_vehicles, c.old_billing_period) || '—'} → {libelleOptions(c.new_extra_vehicles, c.new_billing_period) || '—'}
                </div>
              )}
              <p className="journal-note">{c.note}</p>
            </li>
          ))}
        </ol>
      )}
    </Modale>
  )
}
